import { computed, ref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'

import apiEndpoints from '@dogfy/services/apiEndpoints'
import ApiService from '@dogfy/services/apiService'
import config from '@/config'
import { getCouponDiscountPercent, getCouponSubcategory } from '@dogfy/lib/utils'

import { useDogAdditionStore } from '@dogfy/stores/dogAddition'
import { parsePhoneNumber } from 'awesome-phonenumber'

import { useAcquisitionStore } from './acquisition'
import { useCampaignsStore } from './campaigns'

export const useSignUpStore = defineStore('signUp', () => {
  // State
  // ----------------------
  const dogAdditionStore = useDogAdditionStore()
  const acquisitionStore = useAcquisitionStore()
  const { activeCampaign } = storeToRefs(useCampaignsStore())

  const testingCookie = useCookie('testing', { readonly: true })

  const baseTrialDiscountPercent = ref(config.defaultTrialDiscountPercent.es) // Use Spain value as a fallback.

  // couponData should store any coupon already applied or to be applied.
  const couponData = ref({
    id: '',
    category: '', // campaign, marketing, internal, external
    subcategory: '', // referral, special: (single time, lifetime, expiresAt)
  })

  const leadId = ref('')
  const leadCampaignId = ref('')
  const isPhoneInternallyInvalid = ref(false)
  const isAnonymousLead = ref(false)
  const contactData = ref({
    address: {},
  })
  const subscription = ref(null)
  const billingData = ref(null)
  const acquisition = ref(null)
  const isCustomer = ref(false)
  const recommendedPlan = ref(null)
  const leadAgent = ref(null)
  const source = ref('web')
  const isFormSortRequired = ref(false)

  // Getters
  // ----------------------
  const parsedPhoneNumber = computed(() => {
    const { number } = parsePhoneNumber(contactData.value.phone, { regionCode: contactData.value.country })
    return number?.e164
  })

  const isCouponReferral = computed(() => !!couponData.value.subcategory?.isReferral)
  const isCouponSubscriptionOnly = computed(() => !!couponData.value.subcategory?.isSubscriptionOnly)
  const isCouponSingleTime = computed(() => !!couponData.value.subcategory?.isSingleTime)
  const isCouponLifetime = computed(() => !!couponData.value.subcategory?.isLifetime)
  const isCouponCampaign = computed(() => couponData.value.category === 'campaign')
  const isCouponMarketing = computed(() => couponData.value.category === 'marketing')

  const totalDiscountPercent = computed(() => {
    const defaultTrialDiscountPercent = config.defaultTrialDiscountPercent[contactData.value.country]
    const couponDiscountPercent = getCouponDiscountPercent(couponData.value.id)
    const subcategory = getCouponSubcategory(couponData.value.id)

    const baseDiscountPercent = activeCampaign.value?.baseTrialDiscountPercent || defaultTrialDiscountPercent

    let totalDiscountPercent = subcategory?.isSubscriptionOnly ? baseDiscountPercent : baseDiscountPercent + couponDiscountPercent

    const isActiveCampaignCoupon = !!activeCampaign.value?.coupon && couponData.value.id === activeCampaign.value?.coupon

    if (isActiveCampaignCoupon) {
      const activeCampaignCouponDiscountPercent = getCouponDiscountPercent(activeCampaign.value.coupon)
      const activeCampaignCouponSubcategory = getCouponSubcategory(activeCampaign.value.coupon)

      totalDiscountPercent = activeCampaignCouponSubcategory?.isSubscriptionOnly ? defaultTrialDiscountPercent : defaultTrialDiscountPercent + activeCampaignCouponDiscountPercent
    }

    return Math.min(totalDiscountPercent, 1)
  })

  // Actions
  // ----------------------

  // Coupon management.
  async function fetchCouponData (coupon) {
    if (!coupon) return

    couponData.value.id = coupon.trim().toUpperCase()
    couponData.value.subcategory = getCouponSubcategory(coupon)

    if (isCouponReferral.value) return

    couponData.value.category = await getCouponCategory(coupon)
  }

  async function getCouponCategory (coupon) {
    try {
      const { data } = await ApiService.get(apiEndpoints.coupons.active(coupon), { country: contactData.value.country, leadCreatedAt: subscription.value?.createdAt })
      return config.coupon.categories.includes(data.type) ? data.type : null
    } catch (err) {
      console.error(`  !! Could not get coupon category ${coupon}`, err.message)
      if (err.response.status === 406) {
        // Code coupon-not-applicable is temporarily set here because the API will not send this error anymore.
        // TODO: Develop a centralized error management system.
        err.response.data.code = 'coupon-not-applicable'
      }
      throw err
    }
  }

  async function validateReferralCode (code) {
    try {
      await ApiService.get(apiEndpoints.validations.referral(code))
    } catch (err) {
      console.error(`  !! Could not validate referral code ${code}`, err.message)
      throw err
    }
  }

  async function applyCoupon (coupon) {
    if (!coupon || !leadId.value) return

    const formattedCoupon = coupon.trim().toUpperCase()

    try {
      const { data } = await ApiService.put(apiEndpoints.lead(leadId.value).coupon, { coupon: formattedCoupon })
      subscription.value.coupon = formattedCoupon
      subscription.value.trialDiscount = data.trialDiscount
      subscription.value.subscriptionDiscount = data.subscriptionDiscount
    } catch (err) {
      subscription.value.coupon = null
      console.error(`  !! Could not apply coupon ${coupon} to lead ${leadId.value}`, err.message)
      if (err.response.status === 406) {
        // Code coupon-not-applicable is temporarily set here because the API will not send this error anymore.
        // TODO: Develop a centralized error management system.
        err.response.data.code = 'coupon-not-applicable'
      }
      throw err
    }
  }

  async function deleteCoupon (coupon) {
    if (!coupon) return

    try {
      const { data } = await ApiService.delete(apiEndpoints.lead(leadId.value).coupon)
      couponData.value = {
        id: null,
        category: null,
        subcategory: null,
      }
      subscription.value.coupon = null
      subscription.value.trialDiscount = data.trialDiscount
      subscription.value.subscriptionDiscount = data.subscriptionDiscount
    } catch (err) {
      console.error(`  !! Could not delete coupon ${coupon}`, err.message)
      throw err
    }
  }

  function resetCouponData () {
    couponData.value = {
      id: '',
      category: '',
      subcategory: '',
    }
  }

  // Lead management.
  async function fetchLeadData (id) {
    if (!id) return

    try {
      const { data: leadData } = await ApiService.get(apiEndpoints.lead(id)._)

      const {
        _id,
        dogs,
        givenName,
        familyName,
        address,
        trialDeliveryDate,
        trialAmount,
        trialDiscount,
        subscriptionAmount,
        subscriptionDiscount,
        subscriptionDiscountPercent,
        ordersInCycle,
        coupon,
        isMixedPlan,
        createdAt,
        isAnonymous,
        sales,
        acquisition: leadAcquisition,
        country,
        phone,
        email,
        defaultDeliveryDate,
        campaignId,
      } = leadData

      // This ensures compatibility with older data models
      // where these fields were nested inside 'sharedInfo'.
      leadId.value = _id
      leadCampaignId.value = campaignId
      isAnonymousLead.value = isAnonymous

      contactData.value = {
        givenName: givenName || leadData.sharedInfo?.givenName || '',
        familyName: familyName || leadData.sharedInfo?.familyName || '',
        address: address || leadData.sharedInfo?.address || {},
        country,
        phone,
        email,
      }

      dogAdditionStore.setDogsData(dogs)

      if (dogAdditionStore.dogs.length === 0) {
        dogAdditionStore.dogs = [{}]
      }

      if (phone) {
        const parsedPhone = parsePhoneNumber(phone)
        contactData.value.phone = parsedPhone.number.national.replace(/\s+/g, '')
      }

      subscription.value = {
        trialDeliveryDate: trialDeliveryDate || leadData.sharedInfo?.trialDeliveryDate || defaultDeliveryDate || null,
        trialAmount,
        trialDiscount,
        subscriptionAmount,
        subscriptionDiscount,
        subscriptionDiscountPercent,
        ordersInCycle,
        coupon,
        plan: isMixedPlan ? 'mixed' : 'standard',
        defaultDeliveryDate,
        createdAt,
      }

      acquisition.value = leadAcquisition
      leadAgent.value = sales.assignedTo
    } catch (err) {
      console.error(`  !! Could not fetch lead data ${id}`, err.message)
      throw err
    }
  }

  async function updateLeadData () {
    if (!dogAdditionStore.hasRequiredDogData()) return

    try {
      const { givenName, familyName, address, email, phone, country, zip, hasGift } = contactData.value
      const { plan: subscriptionPlan } = subscription.value

      const { number: phoneNumber } = parsePhoneNumber(phone, { regionCode: country })

      const newLeadData = {
        givenName,
        familyName,
        country,
        zip,
        acquisition: acquisitionStore.getAcquisitionData(),
        hasGift,
        plan: subscriptionPlan,
        dogs: dogAdditionStore.getMappedDogData(),
      }

      contactData.value.address.country = country
      if (hasRequiredAddressData()) {
        newLeadData.address = address
      }

      const leadEndpointParams = {}

      if (isAnonymousLead.value) {
        // Set isAnonymous query param only when required.
        leadEndpointParams.isAnonymous = true
      } else {
        newLeadData.email = email
        newLeadData.phone = phoneNumber?.e164
      }

      if (testingCookie.value?.intercomTest === 'B') {
        newLeadData.email = email
      }

      const { data } = await ApiService.patch(apiEndpoints.lead(leadId.value)._, newLeadData, { params: leadEndpointParams })

      const { trialAmount, trialDiscount, subscriptionAmount, subscriptionDiscount = 0, subscriptionDiscountPercent, defaultDeliveryDate, ordersInCycle } = data

      subscription.value = {
        ...subscription.value,
        trialAmount,
        trialDiscount,
        subscriptionAmount,
        subscriptionDiscount,
        subscriptionDiscountPercent,
        defaultDeliveryDate,
        ordersInCycle,
      }

      const coupon = couponData.value.id || subscription.value.coupon
      if (coupon && coupon !== subscription.value.coupon) {
        await applyCoupon(coupon)
      }

      if (data.dogs?.length > 0) {
        const adjustedDogs = dogAdditionStore.dogs.map((dog, index) => ({ ...dog, ...data.dogs[index] }))
        dogAdditionStore.setDogsData(adjustedDogs)
      }

      contactData.value.country = data.country
      contactData.value.address.country = contactData.value.country

      if (zip) contactData.value.address.zip = zip

      acquisition.value = data.acquisition
    } catch (err) {
      console.error(`  !! Could not update lead ${leadId.value}`, err.message)
      throw err
    }
  }

  async function updateLeadAcquisitionData () {
    try {
      if (!leadId.value) return

      const { data } = await ApiService.put(apiEndpoints.lead(leadId.value).acquisition, acquisitionStore.getAcquisitionData())

      acquisition.value = data.acquisition
    } catch (err) {
      console.error(`  !! Could not update lead acquisition data ${leadId.value}`, err.message)
      throw err
    }
  }

  async function updateDate (date) {
    try {
      await ApiService.get(apiEndpoints.config.deliveryDateAvailability, { date, country: contactData.value.country, type: 'trial' })
      subscription.value.trialDeliveryDate = date
    } catch (err) {
      subscription.value.trialDeliveryDate = subscription.value.defaultDeliveryDate
      console.error(`  !! Could not update date ${date}`, err.message)
      throw err
    }
  }

  async function submitLeadData () {
    try {
      const { email, phone, country, zip, hasGift } = contactData.value

      const isSimplifiedLead = testingCookie.value?.intercomTest !== 'B' && ((!!email && !phone) || (!email && !!phone))

      if (isAnonymousLead.value && leadId.value) {
        return updateLeadData()
      }

      const newLeadData = {
        email,
        country,
        zip,
        acquisition: acquisitionStore.getAcquisitionData(),
        hasGift,
        dogs: dogAdditionStore.getMappedDogData(),
        campaignId: activeCampaign.value?.isApplicable ? activeCampaign.value._id : undefined,
      }

      const { number } = parsePhoneNumber(phone, { regionCode: country })
      newLeadData.phone = number?.e164

      const leadEndpoint = isSimplifiedLead ? apiEndpoints.leads.simplified : apiEndpoints.leads._

      const leadEndpointParams = {}

      if (isAnonymousLead.value) {
        // Set isAnonymous query param only when required.
        leadEndpointParams.isAnonymous = true
      }

      const { data } = await ApiService.post(leadEndpoint, newLeadData, { params: leadEndpointParams })

      const { _id, trialAmount, trialDiscount, subscriptionAmount, defaultDeliveryDate, ordersInCycle } = data

      leadId.value = _id
      subscription.value = {
        ...subscription.value,
        trialAmount,
        trialDiscount,
        subscriptionAmount,
        subscriptionDiscount: 0,
        defaultDeliveryDate,
        ordersInCycle,
        plan: 'standard',
      }

      const coupon = couponData.value.id || subscription.value.coupon
      if (coupon) {
        await applyCoupon(coupon)
      }

      if (data.dogs?.length > 0) {
        const adjustedDogs = dogAdditionStore.dogs.map((dog, index) => ({ ...dog, ...data.dogs[index] }))
        dogAdditionStore.setDogsData(adjustedDogs)
      }

      contactData.value.country = data.country
      contactData.value.address.country = contactData.value.country

      if (zip) contactData.value.address.zip = zip

      acquisition.value = data.acquisition
    } catch (err) {
      if (err.response?.status === 406 && err.response?.data?.code !== 'coupon-not-applicable') {
        isPhoneInternallyInvalid.value = true
      }
      console.error(`  !! Could not submit lead data ${leadId.value}`, err.message)
      throw err
    }
  }

  async function createCustomer () {
    const { givenName, familyName, email, phone, country, address } = contactData.value
    const parsedPhone = parsePhoneNumber(phone, { regionCode: country })

    const { stripeTokenId, cardLast4 } = billingData.value
    const { trialDeliveryDate, plan } = subscription.value

    const newCustomerData = {
      givenName,
      familyName,
      email,
      phone: parsedPhone.number.e164,
      country,
      address: {
        ...address,
        country,
      },
      paymentMethodId: billingData.value.paymentMethodId,
      stripeTokenId,
      cardLast4,
      trialDeliveryDate,
      plan,
      source: source.value,
    }

    try {
      if (subscription.value.coupon) {
        const { isReferral } = getCouponSubcategory(subscription.value.coupon) || {}
        if (isReferral) {
          await validateReferralCode(subscription.value.coupon)
        } else {
          couponData.value.category = await getCouponCategory(subscription.value.coupon)
        }
      }

      try {
        const { data } = await ApiService.post(apiEndpoints.lead(leadId.value).customers, newCustomerData)
        isCustomer.value = true
        return data
      } catch (innerErr) {
        isPhoneInternallyInvalid.value = innerErr.response.status === 406
        throw innerErr
      }
    } catch (err) {
      console.error(`  !! Could not create customer ${leadId.value}`, err.message)
      if (err.response.status === 406 && !isPhoneInternallyInvalid.value) {
        // Code coupon-not-applicable is temporarily set here because the API will not send this error anymore.
        // TODO: Develop a centralized error management system.
        err.response.data.code = 'coupon-not-applicable'
      }
      throw err
    }
  }

  function hasRequiredAddressData () {
    const requiredFields = ['line1', 'locality', 'zip', 'country']

    return requiredFields.every(field => !!contactData.value.address[field])
  }

  function $reset () {
    resetCouponData()

    baseTrialDiscountPercent.value = config.defaultTrialDiscountPercent.es
    leadId.value = ''
    leadCampaignId.value = ''
    isPhoneInternallyInvalid.value = false
    isAnonymousLead.value = false
    contactData.value = {
      address: {},
    }
    subscription.value = null
    billingData.value = null
    acquisition.value = null
    isCustomer.value = false
    recommendedPlan.value = null
    leadAgent.value = null
    source.value = 'web'
    isFormSortRequired.value = false
  }

  return {
    baseTrialDiscountPercent,
    couponData,
    leadId,
    leadCampaignId,
    isPhoneInternallyInvalid,
    isAnonymousLead,
    contactData,
    subscription,
    billingData,
    acquisition,
    isCustomer,
    recommendedPlan,
    leadAgent,
    source,
    isFormSortRequired,
    parsedPhoneNumber,
    isCouponReferral,
    isCouponSubscriptionOnly,
    isCouponSingleTime,
    isCouponLifetime,
    isCouponCampaign,
    isCouponMarketing,
    totalDiscountPercent,
    fetchCouponData,
    applyCoupon,
    deleteCoupon,
    resetCouponData,
    submitLeadData,
    createCustomer,
    updateDate,
    fetchLeadData,
    updateLeadData,
    updateLeadAcquisitionData,
    validateReferralCode,
    getCouponCategory,
    $reset,
  }
},
{
  customPersist: {
    storage: 'localStorage', // Pick between session and local storage.
    paths: [
      'acquisition',
      'baseTrialDiscountPercent',
      'billingData',
      'couponData',
      'isCustomer',
      'isFormSortRequired',
      'isAnonymousLead',
      'leadId',
      'leadAgent',
      'source',
      'subscription',
      'contactData',
    ],
    persist: false, // Allow persistance only when $persist(true) is called.
  },
})
